import { Tag } from "components/designV2/tag/tag";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { useEffect, useState, type ReactNode } from "react";
import styled from "styled-components";

export interface AvatarChipProps {
  title: string;
  subTitle?: string | null;
  avatarUrl?: string | null;
  iconName?: string;
  tags?: string[];
  tagMaxWidth?: number;
  onAvatarClick?: () => void;
  avatarBackgroundColor?: ColorV2;
  titleIcon?: ReactNode;
}

/** Compact avatar chip with title, optional subtitle, and tags. */
export const AvatarChip = ({
  title,
  subTitle,
  avatarUrl,
  iconName,
  tags = [],
  tagMaxWidth,
  onAvatarClick,
  avatarBackgroundColor,
  titleIcon,
}: AvatarChipProps): JSX.Element => {
  const resolvedIconName = iconName ?? "v2/person";
  const resolvedAvatarBackground = avatarBackgroundColor ?? ColorV2.White;
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [avatarUrl]);

  const imageSrc = avatarUrl && !hasImageError ? avatarUrl : null;

  return (
    <Wrapper>
      <Avatar
        role={onAvatarClick ? "button" : undefined}
        $clickable={Boolean(onAvatarClick)}
        onClick={onAvatarClick}
        $backgroundColor={resolvedAvatarBackground}
      >
        {imageSrc ? (
          <AvatarImage
            src={imageSrc}
            alt={title}
            onError={() => setHasImageError(true)}
          />
        ) : (
          <CustomIcon
            name={resolvedIconName}
            size="20px"
            color={ColorV2.Blue3}
          />
        )}
      </Avatar>
      <Content>
        <TitleRow>
          <Title>{title}</Title>
          {titleIcon}
        </TitleRow>
        {(subTitle || tags.length > 0) && (
          <MetaRow>
            {tags.length > 0 && (
              <Tags>
                {tags.map((tag) => (
                  <Tag key={tag} text={tag} maxWidth={tagMaxWidth} />
                ))}
              </Tags>
            )}
            {subTitle && <SubTitle>{subTitle}</SubTitle>}
          </MetaRow>
        )}
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 12px;
  background: transparent;
  width: 100%;
`;

const Avatar = styled.div<{ $clickable: boolean; $backgroundColor: ColorV2 }>`
  min-width: 36px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(props) => props.$backgroundColor};
  border: 1px solid ${ColorV2.Blue6};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.div`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
`;

const TitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SubTitle = styled.div`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
`;

const MetaRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
`;

const Tags = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
